import { memo, useCallback } from 'react';

import { ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { DEFAULT_INTEGRATION_CONFIG } from '@/components/pages/Integration/schemas';
import { focusVisibleRing } from '@/components/ui/constants';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Icon as IconType } from '@/shared/types/icon';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { timeFromNow } from '@/util/dateUtils';

import IntegrationStatus from './IntegrationStatus';

// Campaign channels are the ones that should show the "Useful for campaigns" label
const CAMPAIGN_CHANNELS: IntegrationType[] = [
  IntegrationType.WHATSAPP,
  IntegrationType.INFOBIP_SMS,
  IntegrationType.INFOBIP_RCS,
  IntegrationType.EMAIL,
  IntegrationType.TWILIO_VOICE,
  IntegrationType.TELNYX_VOICE,
  IntegrationType.MSTAT_VIBER,
];

interface IntegrationItemProps {
  type: IntegrationType;
  name?: string;
  Icon?: React.FC<IconType>;
  connected?: Integration;
}

/**
 * IntegrationItem - Individual integration item component
 *
 * Displays a single integration with its status, icon, and action button.
 * Can represent either a connected integration (with status) or an available
 * integration (with connect option).
 *
 * Features:
 * - Shows integration status (active/disabled) for connected integrations
 * - Displays "Useful for campaigns" label for specific integrations
 * - Handles navigation to integration details or connection flow (if not connected)
 *
 * @param {IntegrationType} type - The type of integration (e.g., 'whatsapp', 'zendesk')
 * @param {string} [name] - Display name for the integration (optional)
 * @param {React.FC<IconType>} [Icon] - Icon component to display (optional)
 * @param {Integration} [connected] - Connected integration data (optional, if not provided shows as available)
 * @returns {JSX.Element} The integration item component
 */
const IntegrationItem = memo(({ type, name, Icon, connected }: IntegrationItemProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);
  const { createIntegration, createStatus } = useIntegrations(deskId);
  const navigate = useNavigate();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const isCreating = createStatus === 'pending';

  const handleConnect = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (isCreating) {
        return;
      }

      // Get default config for this integration type, or use empty object.
      // Custom and mstat-viber are created WITHOUT a config: their config
      // schemas reject an empty object (Custom requires webhook_url; mstat-viber
      // requires api_token XOR username+password), but accept an absent config.
      // The config is filled in later on the integration page.
      const skipConfigOnCreate =
        type === IntegrationType.CUSTOM || type === IntegrationType.MSTAT_VIBER;
      const defaultConfig = DEFAULT_INTEGRATION_CONFIG[type]?.config || {};
      const integrationPayload = skipConfigOnCreate
        ? { name: type, type, desk_id: deskId }
        : { name: type, type, desk_id: deskId, config: defaultConfig };

      createIntegration(integrationPayload, {
        onSuccess: ({ integration_id }) => {
          navigate(`/${slug}/environments/${deskId}/integrations/${integration_id}`);
        },
      });
    },
    [createIntegration, deskId, isCreating, navigate, slug, type]
  );

  const isDisabled = !connected && (!canWrite || isCreating);

  const className = cn(
    'group flex w-full items-center gap-4 rounded-lg border border-border bg-background px-4 py-4 transition-colors',
    focusVisibleRing,
    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted/50'
  );

  const content = (
    <>
      {/* Icon */}
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border-[0.5px] border-border bg-subtle p-2">
        <div className="flex items-center justify-center rounded bg-background p-1 shadow-sm">
          <Icon className="h-6 w-6" />
        </div>
      </div>

      {/* Name and additional info */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <div className="flex items-center gap-3">
          <span className="text-base leading-none font-medium text-foreground">{name}</span>
          {connected && <IntegrationStatus integration={connected} />}
        </div>
        {connected && (
          <span className="truncate text-sm leading-5 font-normal text-muted-foreground">
            {connected.last_session_at ? (
              <TimestampTooltip timestamp={connected.last_session_at}>
                {`${t('connections.last_conversation')}: ${timeFromNow(connected.last_session_at)}`}
              </TimestampTooltip>
            ) : (
              t('connections.no_conversations_yet')
            )}
          </span>
        )}
        {!connected && CAMPAIGN_CHANNELS.includes(type) && (
          <span className="truncate text-sm leading-5 font-normal text-muted-foreground">
            {t('connections.useful_for_campaigns')}
          </span>
        )}
      </div>

      {/* Chevron or Connect Text */}
      {connected ? (
        <ChevronRightIcon
          size={24}
          className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
        />
      ) : (
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="text-sm text-primary">{t('connections.connect')}</span>
          <ExternalLinkIcon className="h-4 w-4 text-primary" />
        </div>
      )}
    </>
  );

  // Render disabled integrations as div
  if (isDisabled) {
    return <div className={className}>{content}</div>;
  }

  // Render connected integrations as Link for navigation
  if (connected) {
    return (
      <Link to={connected.integration_id} className={className}>
        {content}
      </Link>
    );
  }

  // Render available integrations as Link for connect action
  return (
    <Link to="" onClick={handleConnect} className={className}>
      {content}
    </Link>
  );
});

IntegrationItem.displayName = 'IntegrationItem';

export default IntegrationItem;
